import React, { useState, useCallback } from "react";
import {
    KeyboardAvoidingView,
    Platform,
    ScrollView,
    StyleSheet,
    Text,
    View,
} from "react-native";
import {
    NavigationProp,
    RouteProp,
    useNavigation,
    useRoute,
} from "@react-navigation/native";
import { AuthStackParams } from "../../navigation/Auth/AuthStackParams";
import { LoginRoute } from "../../navigation/Auth/routes";
import { Button, Gap } from "../../components";
import { ButtonType } from "../../theme";
import CurvedBgImage from "../../components/CurvedBgImage";
import TextInput from "../../components/TextInput";
import CustomAlert from "../../components/CoustmizeAlertmsg";
import { useTheme, AppTheme } from "../../theme/colors";
import { completeNewPassword } from "../../utils/cognitoAuth";

const ChangePassword = () => {
    const route = useRoute<RouteProp<AuthStackParams, "ChangePassword">>();
    const { user } = route.params; // The Cognito user passed from login screen
    const navigation = useNavigation<NavigationProp<AuthStackParams>>();
    const theme = useTheme();

    const [password, setPassword] = useState<string>("");
    const [confirmPassword, setConfirmPassword] = useState<string>("");
    const [error, setError] = useState<{ title: string; message: string } | null>(
      null
    );
    const [successVisible, setSuccessVisible] = useState(false);
    const [loading, setLoading] = useState(false);

    const styles = getStyles(theme);

    const handleChangePassword = useCallback(async () => {
        if (!password || !confirmPassword) {
            setError({
                title: "Missing Information",
                message: "Please enter and confirm your new password.",
            });
            return;
        }
        if (password !== confirmPassword) {
            setError({
                title: "Password Mismatch",
                message: "The passwords do not match. Please try again.",
            });
            return;
        }

        try {
            setLoading(true);
            await completeNewPassword(user, password);
            setSuccessVisible(true);
        } catch (err: any) {
            setError({
                title: "Password Update Failed",
                message: err.message || "Unable to update your password. Try again.",
            });
        } finally {
            setLoading(false);
        }
    }, [password, confirmPassword, user]);

    const goBack = () => {
        navigation.goBack();
    };

    return (
      <>
          <KeyboardAvoidingView
            style={{ flex: 1, backgroundColor: theme.BACKGROUND }}
            behavior={Platform.select({ ios: "padding", android: "height" })}
          >
              <CurvedBgImage />
              <View style={styles.header}>
                  <View
                    style={{
                        flex: 1,
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                  >
                      <Text style={styles.heading}>Change Password</Text>
                  </View>
              </View>

              <ScrollView>
                  <View style={{ flex: 1, padding: 20, justifyContent: "center" }}>
                      <Text style={{ fontSize: 30, fontWeight: "600" }}>
                          Please set a new password to continue.
                      </Text>
                      <View style={{ marginTop: 50, gap: 20 }}>
                          <TextInput
                            autoCorrect={false}
                            autoCapitalize="none"
                            placeholder="Enter New Password"
                            label="New Password"
                            secureTextEntry
                            value={password}
                            onChangeText={(text) => setPassword(text)}
                          />
                          <TextInput
                            autoCorrect={false}
                            autoCapitalize="none"
                            placeholder="Confirm New Password"
                            label="Confirm Password"
                            secureTextEntry
                            value={confirmPassword}
                            onChangeText={(text) => setConfirmPassword(text)}
                          />
                      </View>

                      <View style={{ alignItems: "center", marginTop: 50 }}>
                          <Button
                            onPress={handleChangePassword}
                            title="Change Password"
                            variant={ButtonType.secondary}
                            loading={loading}
                          />
                          <Gap gap={20} />
                          <Button
                            title="Go Back"
                            variant={ButtonType.primary}
                            onPress={goBack}
                          />
                      </View>
                  </View>
              </ScrollView>
          </KeyboardAvoidingView>

          {/* ✅ Success Alert */}
          <CustomAlert
            cancelText="cancel"
            confirmText="OK"
            visible={successVisible}
            showCancelButton={false}
            title="Password Updated"
            message="Your password has been successfully changed. You can now log in with your new password."
            onCancel={() => setSuccessVisible(false)}
            onConfirm={() => {
                setSuccessVisible(false);
                navigation.navigate(LoginRoute);
            }}
          />

          {/* ❌ Error Alert */}
          <CustomAlert
            cancelText="cancel"
            confirmText="OK"
            visible={!!error}
            showCancelButton={false}
            title={error?.title || ""}
            message={error?.message || ""}
            onCancel={() => setError(null)}
            onConfirm={() => setError(null)}
          />
      </>
    );
};

export default ChangePassword;

const getStyles = (theme: AppTheme) =>
  StyleSheet.create({
      header: {
          height: 95,
          borderBottomStartRadius: 50,
          borderBottomEndRadius: 50,
      },
      heading: {
          fontSize: 16,
          fontWeight: "600",
          color: theme.TEXT,
      },
  });